import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Segmented, Select, Switch, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus, Sprout } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  COLOUR_GROUP_KINDS,
  ColourClient,
  ColourSchemeClient,
  PartColourGroupClient,
  type ApplyReportDTO,
  type ColourDTO,
  type ColourGroupKind,
  type ColourSchemeDTO,
  type CoverageDTO,
} from "../../api/configurator";
import { refusalMessage } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { COLOURS_KEY, colourGroupKindLabel, useColours } from "./PartColoursTab";

type View = "colours" | "schemes" | "links";

/**
 * The colours (design §2, §3): each panel colour once, with its SKU code and
 * every way the data spells it; the colour schemes (a product's COLOR value as
 * its colours); and the sister links per kind, as a grid of groups against
 * colours, with the parts not linked yet. Adding a colour (§7) starts here.
 */
export function ColoursPage() {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  const [view, setView] = useState<View>(() => {
    const wanted = new URLSearchParams(window.location.search).get("view");
    return wanted === "schemes" || wanted === "links" ? wanted : "colours";
  });
  const title = t("navigation.item.engineering-colours", "Colours");
  if (!hasView(NexpimView.MANUFACTURING_PARTS)) {
    return (
      <>
        <PageHeader title={title} />
        <p className="p-4 text-[13px] text-ink-muted lg:p-6">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "colour.page.subtitle", "Part colours: each panel colour once, with its SKU code and its spellings. Product colours: which part colours a product's COLOR is made in. Sister links tie a part to the same part in the other colours.",
        )}
        actions={<SeedButton />}
      />
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2 lg:px-6">
        <Segmented<View>
          value={view}
          onChange={setView}
          options={[
            { value: "colours", label: t("colour.view.colours", "Part colours") },
            { value: "schemes", label: t("colour.view.schemes", "Product colours") },
            { value: "links", label: t("colour.view.links", "Sister links") },
          ]}
        />
      </div>
      {view === "colours" && <ColoursView />}
      {view === "schemes" && <SchemesView />}
      {view === "links" && <LinksView />}
    </>
  );
}

/** Fills what is empty from the data (colours from finishes, part colours, links); a dry run first. */
function SeedButton() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new ColourClient(http), [http]);
  const seed = useMutation({
    mutationFn: async () => {
      const preview = await client.seed(true);
      const ok = await confirm({
        title: t("colour.seed.title", "Fill the colour data from what the data says?"),
        message: describeReport(preview, t),
        confirmLabel: t("colour.seed.confirm", "Fill it"),
      });
      return ok ? client.seed(false) : null;
    },
    onSuccess: async (report) => {
      if (report) {
        toast.success(t("colour.seed.done", "Colour data filled: {{n}} parts coloured.", { n: report.counts.coloured_parts ?? 0 }));
        await queryClient.invalidateQueries({ queryKey: COLOURS_KEY });
      }
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  if (!canWrite) return null;
  return (
    <Button variant="secondary" icon={<Sprout className="size-4" />} loading={seed.isPending} onClick={() => seed.mutate()}
      title={t("colour.seed.hint", "Only fills what is empty: nothing set by hand changes.")}>
      {t("colour.seed", "Fill from the data")}
    </Button>
  );
}

function describeReport(report: ApplyReportDTO, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): string {
  const counts = Object.entries(report.counts).filter(([, n]) => n > 0).map(([key, n]) => `${key.replace(/_/g, " ")}: ${String(n)}`);
  const problems = report.problems.length > 0 ? ` · ${t("colour.seed.problems", "{{n}} to look at", { n: report.problems.length })}` : "";
  return (counts.length > 0 ? counts.join(" · ") : t("colour.seed.nothing", "Nothing to fill.")) + problems;
}

// ── colours ───────────────────────────────────────────────────────────────

function ColoursView() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new ColourClient(http), [http]);
  const colours = useColours();
  const [editing, setEditing] = useState<ColourDTO | null>(null);
  const remove = useMutation({
    mutationFn: (colour: ColourDTO) => client.remove(colour.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COLOURS_KEY }),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const columns = useMemo(() => {
    const c = defineColumns<ColourDTO>();
    return [
      c.text("code", (row) => row.code, { header: t("colour.code", "Code"), priority: 1 }),
      c.text("sku_code", (row) => row.sku_code ?? "", { header: t("colour.sku-code", "SKU code"), priority: 1 }),
      c.text("name_fr", (row) => row.name_fr ?? "", { header: t("colour.name-fr", "Name (FR)"), priority: 2 }),
      c.text("name_en", (row) => row.name_en ?? "", { header: t("colour.name-en", "Name (EN)"), priority: 2 }),
      c.text("aliases", (row) => row.aliases.filter((one) => one.value !== row.code).map((one) => one.value).join(", "), {
        header: t("colour.aliases", "Also written"),
        priority: 3,
      }),
      c.number("part_count", (row) => row.part_count ?? 0, { header: t("colour.parts", "Parts"), priority: 2 }),
      c.number("product_count", (row) => row.product_count ?? 0, { header: t("colour.products", "Products"), priority: 2 }),
      c.custom("is_active", (row) => (row.is_active ? "1" : "0"), (row) =>
        row.is_active ? <Badge size="sm" variant="success">{t("colour.active", "Offered")}</Badge> : <Badge size="sm" variant="neutral">{t("colour.inactive", "Not offered")}</Badge>,
        { header: t("colour.state", "State"), priority: 3 }),
    ];
  }, [t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ColourDTO>
        data={colours.data ?? []}
        columns={columns}
        loading={colours.isLoading}
        getRowId={(row) => String(row.id)}
        stateKey="engineering.colours"
        toolbarExtra={
          canWrite ? (
            <Button size="sm" variant="primary" icon={<Plus className="size-4" />}
              onClick={() => setEditing({ id: 0, code: "", name_fr: "", name_en: "", sku_code: null, is_active: true, note: null, aliases: [] })}>
              {t("colour.new", "New colour")}
            </Button>
          ) : undefined
        }
        rowActions={(row) =>
          canWrite ? (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>{t("button.edit", "Edit")}</Button>
              {(row.part_count ?? 0) === 0 && (
                <Button size="sm" variant="ghost" onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: t("colour.delete.title", "Delete {{code}}?", { code: row.code }),
                      message: t("colour.delete.message", "No part is in this colour. A product colour made in it has to be changed first."),
                      confirmLabel: t("colour.delete.confirm", "Delete"),
                      variant: "danger",
                    });
                    if (ok) remove.mutate(row);
                  })();
                }}>
                  {t("button.delete", "Delete")}
                </Button>
              )}
            </div>
          ) : null
        }
        emptyMessage={t("colour.empty", "No colour yet. Fill from the data makes one per virtual panel finish.")}
      />
      {editing && <ColourDialog colour={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ColourDialog({ colour, onClose }: { colour: ColourDTO; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ColourClient(http), [http]);
  const [code, setCode] = useState(colour.code);
  const [nameFr, setNameFr] = useState(colour.name_fr ?? "");
  const [nameEn, setNameEn] = useState(colour.name_en ?? "");
  const [sku, setSku] = useState(colour.sku_code ?? "");
  const [active, setActive] = useState(colour.is_active);
  const [note, setNote] = useState(colour.note ?? "");
  const [finishes, setFinishes] = useState(colour.aliases.filter((one) => one.kind === "FINISH" && one.value !== colour.code).map((one) => one.value).join("\n"));
  const [names, setNames] = useState(colour.aliases.filter((one) => one.kind === "NAME").map((one) => one.value).join("\n"));
  const lines = (text: string) => text.split("\n").map((one) => one.trim()).filter(Boolean);
  const renaming = colour.id !== 0 && code.trim().toUpperCase() !== colour.code;
  const save = useMutation({
    mutationFn: () => {
      const body: ColourDTO = {
        ...colour,
        code: code.trim(),
        name_fr: nameFr.trim() || null,
        name_en: nameEn.trim() || null,
        sku_code: sku.trim() || null,
        is_active: active,
        note: note.trim() || null,
        aliases: [
          ...lines(finishes).map((value) => ({ kind: "FINISH" as const, value })),
          ...lines(names).map((value) => ({ kind: "NAME" as const, value })),
        ],
      };
      return colour.id === 0 ? client.create(body).then(() => undefined) : client.update(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COLOURS_KEY });
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={colour.id === 0 ? t("colour.new", "New colour") : colour.code}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>{t("dialog.cancel", "Cancel")}</Button>
          <Button variant="primary" loading={save.isPending} disabled={code.trim() === "" || (sku.trim() !== "" && !/^\d{2}$/.test(sku.trim()))}
            onClick={() => save.mutate()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("colour.code", "Code")} htmlFor="colour-code" required
          hint={renaming ? t("colour.code.rename", "Its virtual panels take the new finish; the old one stays as a spelling.") : t("colour.code.hint", "The finish as panels say it: FASHIONISTA.")}>
          <Input id="colour-code" value={code} onChange={(event) => setCode(event.target.value)} autoFocus />
        </Field>
        <Field label={t("colour.sku-code", "SKU code")} htmlFor="colour-sku" hint={t("colour.sku-code.hint", "The two digits its SKUs end with: 88.")}>
          <Input id="colour-sku" value={sku} inputMode="numeric" maxLength={2} onChange={(event) => setSku(event.target.value)} />
        </Field>
        <Field label={t("colour.name-fr", "Name (FR)")} htmlFor="colour-fr">
          <Input id="colour-fr" value={nameFr} onChange={(event) => setNameFr(event.target.value)} />
        </Field>
        <Field label={t("colour.name-en", "Name (EN)")} htmlFor="colour-en">
          <Input id="colour-en" value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
        </Field>
        <Field label={t("colour.finishes", "Other finish spellings")} htmlFor="colour-finishes"
          hint={t("colour.finishes.hint", "One per line. A panel written this way is this colour's panel.")}>
          <Textarea id="colour-finishes" rows={3} value={finishes} onChange={(event) => setFinishes(event.target.value)} />
        </Field>
        <Field label={t("colour.names", "Words in part names")} htmlFor="colour-names"
          hint={t("colour.names.hint", "One per line: BLACK KARISMA on an edgeband, SHAKER on a cap.")}>
          <Textarea id="colour-names" rows={3} value={names} onChange={(event) => setNames(event.target.value)} />
        </Field>
        <Field label={t("colour.note", "Note")} htmlFor="colour-note">
          <Input id="colour-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <div className="flex items-end pb-2">
          <Switch checked={active} onChange={setActive} label={t("colour.offered", "Offered: products are made in it")} />
        </div>
      </div>
    </Dialog>
  );
}

// ── schemes ───────────────────────────────────────────────────────────────

function SchemesView() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const client = useMemo(() => new ColourSchemeClient(http), [http]);
  const schemes = useQuery({ queryKey: [...COLOURS_KEY, "scheme"], queryFn: () => client.findAll() });
  const [editing, setEditing] = useState<ColourSchemeDTO | null>(null);
  const columns = useMemo(() => {
    const c = defineColumns<ColourSchemeDTO>();
    return [
      c.text("sales_name", (row) => row.sales_name, { header: t("scheme.sales-name", "Product colour"), priority: 1 }),
      c.text("primary", (row) => row.primary_code ?? t("scheme.not-set", "not set"), { header: t("scheme.primary", "Main part colour"), priority: 1, filter: "select" }),
      c.text("accent", (row) => row.accent_code ?? "", { header: t("scheme.accent", "Accent part colour"), priority: 2, filter: "select" }),
      c.number("product_count", (row) => row.product_count ?? 0, { header: t("scheme.products", "Products"), priority: 2 }),
      c.text("note", (row) => row.note ?? "", { header: t("colour.note", "Note"), defaultVisible: false }),
    ];
  }, [t]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ColourSchemeDTO>
        data={schemes.data ?? []}
        columns={columns}
        loading={schemes.isLoading}
        getRowId={(row) => String(row.id)}
        stateKey="engineering.colour-schemes"
        toolbarLead={<span className="text-[12px] text-ink-muted">{t("scheme.hint", "\"Walnut & White\" and \"White & Walnut\" are two product colours: the main part colour is the one most panels are in.")}</span>}
        toolbarExtra={
          canWrite ? (
            <Button size="sm" variant="primary" icon={<Plus className="size-4" />}
              onClick={() => setEditing({ id: 0, sales_name: "", primary_colour_id: null, accent_colour_id: null, note: null })}>
              {t("scheme.new", "New product colour")}
            </Button>
          ) : undefined
        }
        rowActions={(row) => (canWrite ? <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>{t("button.edit", "Edit")}</Button> : null)}
        emptyMessage={t("scheme.empty", "No product colour yet. Fill from the data reads them from the products' pieces.")}
      />
      {editing && <SchemeDialog scheme={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SchemeDialog({ scheme, onClose }: { scheme: ColourSchemeDTO; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new ColourSchemeClient(http), [http]);
  const colours = useColours();
  const options = (colours.data ?? []).map((colour) => ({ value: String(colour.id), label: colour.code }));
  const [salesName, setSalesName] = useState(scheme.sales_name);
  const [primary, setPrimary] = useState(scheme.primary_colour_id == null ? "" : String(scheme.primary_colour_id));
  const [accent, setAccent] = useState(scheme.accent_colour_id == null ? "" : String(scheme.accent_colour_id));
  const [note, setNote] = useState(scheme.note ?? "");
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: COLOURS_KEY });
    onClose();
  };
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const save = useMutation({
    mutationFn: () => {
      const body: ColourSchemeDTO = {
        ...scheme,
        sales_name: salesName.trim(),
        primary_colour_id: primary === "" ? null : Number(primary),
        accent_colour_id: accent === "" ? null : Number(accent),
        note: note.trim() || null,
      };
      return scheme.id === 0 ? client.create(body).then(() => undefined) : client.update(body);
    },
    onSuccess: done,
    onError: fail,
  });
  const remove = useMutation({ mutationFn: () => client.remove(scheme.id), onSuccess: done, onError: fail });
  return (
    <Dialog
      open
      onClose={onClose}
      title={scheme.id === 0 ? t("scheme.new", "New product colour") : scheme.sales_name}
      actions={
        <>
          {scheme.id !== 0 && (
            <Button variant="danger" className="mr-auto" onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: t("scheme.delete.title", "Delete the product colour {{name}}?", { name: scheme.sales_name }),
                  message: t("scheme.delete.in-use", "Only a product colour no product has can be deleted: give its products another colour first."),
                  confirmLabel: t("colour.delete.confirm", "Delete"),
                  variant: "danger",
                });
                if (ok) remove.mutate();
              })();
            }}>
              {t("button.delete", "Delete")}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>{t("dialog.cancel", "Cancel")}</Button>
          <Button variant="primary" loading={save.isPending}
            disabled={salesName.trim() === "" || (accent !== "" && (primary === "" || primary === accent))} onClick={() => save.mutate()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label={t("scheme.sales-name", "Product colour")} htmlFor="scheme-name" required hint={t("scheme.sales-name.hint", "As the products' COLOR says it: Brown Oak.")}>
          <Input id="scheme-name" value={salesName} onChange={(event) => setSalesName(event.target.value)} autoFocus />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("scheme.primary", "Main part colour")} htmlFor="scheme-primary"
            hint={t("scheme.primary.hint", "Empty until you know: no model in it can be synced or created before.")}>
            <Select id="scheme-primary" options={options} placeholder={t("colour.part.none", "No colour")} value={primary} onChange={(event) => setPrimary(event.target.value)} />
          </Field>
          <Field label={t("scheme.accent", "Accent part colour")} htmlFor="scheme-accent" hint={t("scheme.accent.hint", "The second part colour of a two-colour product.")}>
            <Select id="scheme-accent" options={options} placeholder={t("scheme.no-accent", "None")} value={accent} onChange={(event) => setAccent(event.target.value)} />
          </Field>
        </div>
        <Field label={t("colour.note", "Note")} htmlFor="scheme-note">
          <Input id="scheme-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ── links ─────────────────────────────────────────────────────────────────

/**
 * The sister groups of a kind against the colours: a part where the group
 * has one, a dot where not. Only colours some group of that kind uses are
 * columns. Below, the coloured parts no group holds yet, and the parts of
 * that kind with no colour: each to settle on its part's Colours tab.
 */
function LinksView() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new PartColourGroupClient(http), [http]);
  const [kind, setKind] = useState<ColourGroupKind>("PANEL");
  const [filter, setFilter] = useState("");
  const coverage = useQuery({ queryKey: [...COLOURS_KEY, "coverage", kind], queryFn: () => client.coverage(kind) });
  const data: CoverageDTO | undefined = coverage.data;
  const used = useMemo(() => {
    if (!data) return [];
    const ids = new Set<string>();
    data.groups.forEach((group) => Object.keys(group.members).forEach((id) => ids.add(id)));
    return data.colours.filter((colour) => ids.has(String(colour.id)));
  }, [data]);
  const groups = (data?.groups ?? []).filter((group) => filter.trim() === "" || group.name.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 lg:px-6">
        <Segmented<ColourGroupKind>
          value={kind}
          onChange={setKind}
          options={COLOUR_GROUP_KINDS.map((one) => ({ value: one, label: colourGroupKindLabel(one, t) }))}
        />
        <Input className="max-w-64" placeholder={t("colour.links.filter", "Filter the groups")} value={filter} onChange={(event) => setFilter(event.target.value)} />
        {data && (
          <span className="text-[13px] text-ink-muted">
            {t("colour.links.counts", "{{groups}} groups · {{unlinked}} not linked · {{uncoloured}} without a colour", {
              groups: data.groups.length,
              unlinked: data.unlinked.length,
              uncoloured: data.uncoloured.length,
            })}
          </span>
        )}
      </div>
      {coverage.isLoading && <p className="px-4 text-[13px] text-ink-muted lg:px-6">{t("table.loading", "Loading")}</p>}
      {data && groups.length > 0 && (
        <div className="overflow-auto px-4 lg:px-6">
          <table className="border-separate border-spacing-0 text-[12px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-surface px-2 py-1 text-left font-semibold">{t("colour.links.group", "Group")}</th>
                {used.map((colour) => (
                  <th key={colour.id} className="whitespace-nowrap px-2 py-1 text-left font-medium text-ink-muted">
                    {colour.code}
                    {colour.sku_code && <span className="ml-1 opacity-60">{colour.sku_code}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="hover:bg-surface-sunk">
                  <td className="sticky left-0 z-10 max-w-80 truncate border-t border-line bg-surface px-2 py-1 font-medium" title={group.name}>{group.name}</td>
                  {used.map((colour) => {
                    // The colour's active part, then its retired ones struck through.
                    const members = group.members[String(colour.id)] ?? [];
                    return (
                      <td key={colour.id} className="whitespace-nowrap border-t border-line px-2 py-1 font-mono">
                        {members.length > 0 ? (
                          <span className="flex flex-col">
                            {members.map((member) => (
                              <Link key={member.id} to="/manufacturing/parts/$partId" params={{ partId: String(member.id) }} search={{ tab: "colours" }}
                                title={member.status === "ACTIVE" ? undefined : t("colour.links.retired", "Inactive: kept beside the active part")}
                                className={member.status === "ACTIVE" ? "text-accent hover:underline" : "text-ink-muted line-through hover:underline"}>
                                {member.number}
                              </Link>
                            ))}
                          </span>
                        ) : (
                          <span className="text-ink-muted opacity-50">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="grid gap-6 px-4 py-4 lg:grid-cols-2 lg:px-6">
          <PartList title={t("colour.links.unlinked", "Coloured, not linked")} parts={data.unlinked} showColour />
          <PartList title={t("colour.links.uncoloured", "No colour, not marked neutral")} parts={data.uncoloured} showColour={false} />
        </div>
      )}
    </div>
  );
}

function PartList({ title, parts, showColour }: { title: string; parts: CoverageDTO["unlinked"]; showColour: boolean }) {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className="mb-2 text-[13px] font-semibold">{title} <span className="font-normal text-ink-muted">{parts.length}</span></h3>
      {parts.length === 0 ? (
        <p className="text-[13px] text-ink-muted">{t("colour.links.none", "None.")}</p>
      ) : (
        <ul className="max-h-96 divide-y divide-line overflow-auto rounded-md border border-line text-[13px]">
          {parts.map((part) => (
            <li key={part.id} className="flex items-center gap-2 px-2 py-1">
              <OpenRecord target={{ kind: "part", id: part.id, tab: "colours" }} label={part.number} />
              <span className="font-mono">{part.number}</span>
              <span className="min-w-0 flex-1 truncate">{part.name}</span>
              {showColour && part.colour_code && <Badge size="sm" variant="neutral">{part.colour_code}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
